import type { CleanPasteConfig } from './cleanpasteconfig';

/**
 * Cleans inline styles from pasted content.
 *
 * @param style The style string to clean
 * @param config The clean paste configuration
 * @param preserveStructuralStyles Whether to preserve structural styles (for lists/tables)
 * @returns The cleaned style string
 */
export function cleanPastedStyles(
	style: string,
	config: CleanPasteConfig,
	preserveStructuralStyles = false
): string {
	if ( !style ) {
		return '';
	}

	const styles = style.split( ';' ).map( s => s.trim() ).filter( s => s );
	const cleanedStyles: Array<string> = [];

	for ( const styleDeclaration of styles ) {
		const [ property, value ] = styleDeclaration.split( ':' ).map( s => s.trim() );

		if ( !property || !value ) {
			continue;
		}

		// Skip styles based on configuration
		if ( config.removeFontFamilies && property === 'font-family' ) {
			continue;
		}

		if ( config.removeColors && property === 'color' ) {
			continue;
		}

		if ( config.removeBackgroundColors &&
			( property === 'background-color' || property === 'background' ) ) {
			continue;
		}

		if ( config.removeLineHeights && property === 'line-height' ) {
			continue;
		}

		// For structural elements, be more lenient with margins/padding
		if ( preserveStructuralStyles ) {
			// Keep list-style properties for lists
			if ( property.startsWith( 'list-style' ) ) {
				cleanedStyles.push( styleDeclaration );
				continue;
			}

			// Keep border properties for tables
			if ( property.startsWith( 'border' ) ) {
				cleanedStyles.push( styleDeclaration );
				continue;
			}
		}

		// Skip margin/padding if configured to remove them
		if ( config.removeMargins && property.indexOf( 'margin' ) !== -1 ) {
			continue;
		}

		if ( config.removePadding && property.indexOf( 'padding' ) !== -1 ) {
			continue;
		}

		// Check for allowed properties that should be preserved
		const allowedProperties = [
			'text-align',
			'text-decoration',
			'font-weight',
			'font-style',
			'text-transform',
			'vertical-align',
			'width',
			'height',
			'border-collapse',
			'border-spacing'
		];

		// For CKEditor-specific styles, check if they match allowed values
		if ( property === 'font-size' ) {
			const allowedSizes = [ '9px', '11px', '13px', '14px', '17px', '19px', '21px' ];
			if ( allowedSizes.indexOf( value ) !== -1 ) {
				cleanedStyles.push( styleDeclaration );
			}
			continue;
		}

		// For colors, only allow HSL colors that match CKEditor's palette
		if ( ( property === 'color' || property === 'background-color' ) &&
			!config.removeColors && !config.removeBackgroundColors ) {
			if ( /^hsl\(\d+,\s*\d+%,\s*\d+%\)$/.test( value ) ) {
				cleanedStyles.push( styleDeclaration );
			}
			continue;
		}

		// Keep other allowed properties
		if ( allowedProperties.indexOf( property ) !== -1 ) {
			cleanedStyles.push( styleDeclaration );
		}
	}

	return cleanedStyles.join( '; ' );
}

/**
 * Cleans HTML string at text level before parsing.
 *
 * @param html The HTML string to clean
 * @param config The clean paste configuration
 * @returns The cleaned HTML string
 */
export function cleanHtmlString( html: string, config: CleanPasteConfig ): string {
	let cleanedHtml = html;

	// Remove font-family styles
	if ( config.removeFontFamilies ) {
		cleanedHtml = cleanedHtml.replace( /font-family\s*:\s*[^;]+;?/gi, '' );
	}

	// Remove color styles
	if ( config.removeColors ) {
		cleanedHtml = cleanedHtml.replace( /(?<!background-)color\s*:\s*[^;]+;?/gi, '' );
	}

	// Remove background colors
	if ( config.removeBackgroundColors ) {
		cleanedHtml = cleanedHtml.replace( /background(?:-color)?\s*:\s*[^;]+;?/gi, '' );
	}

	// Remove line-height styles
	if ( config.removeLineHeights ) {
		cleanedHtml = cleanedHtml.replace( /line-height\s*:\s*[^;]+;?/gi, '' );
	}

	// Remove margin styles
	if ( config.removeMargins ) {
		cleanedHtml = cleanedHtml.replace( /margin(?:-[a-z]+)?\s*:\s*[^;]+;?/gi, '' );
	}

	// Remove padding styles
	if ( config.removePadding ) {
		cleanedHtml = cleanedHtml.replace( /padding(?:-[a-z]+)?\s*:\s*[^;]+;?/gi, '' );
	}

	// Remove Microsoft Word specific attributes
	cleanedHtml = cleanedHtml
		.replace( /\s*data-mce-[^=]*="[^"]*"/gi, '' )
		.replace( /\s*xmlns[^=]*="[^"]*"/gi, '' )
		// Remove empty style attributes
		.replace( /\s*style\s*=\s*["'][\s;]*["']/gi, '' )
		// Clean up multiple spaces
		.replace( /\s+/g, ' ' );

	return cleanedHtml.trim();
}
